/* eslint-disable react/prop-types */
import React, { useState } from 'react';

import { Grid, IconButton, Popover } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import FullscreenExitIcon from '@material-ui/icons/FullscreenExit';
import {
  mdiDelete,
  mdiDownload,
  mdiEmoticon,
  mdiEyeCircleOutline,
  mdiFormatText,
  mdiMapMarkerPlus,
  mdiPencil,
  mdiRotateLeftVariant,
  mdiRotateRightVariant,
} from '@mdi/js';
import Icon from '@mdi/react';
import clsx from 'clsx';
import Button from 'components/Button/Button';
import { PlaceOnFloorplanDialog } from 'components/PlaceOnFloorplanDialog/PlaceOnFloorplanDialog';
import ReactionPalette from 'components/PreviewDialog/components/ReactionPalette';
import Spinner from 'components/Spinner/Spinner';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectProjectFloorplansArray } from 'store/floorplans/selectors/floorplans.selectors';
import { ImageMediaTags } from './ImageMediaTags/ImageMediaTags';
import { IMAGE_PREVIEW_CAPABILITIES_ALL } from './imagePreviewCapabilities';
import {
  PHONE_BAR_BUTTON,
  PHONE_BAR_HEIGHT,
  PHONE_BAR_INFO_ROOM,
  PHONE_BAR_INSET,
} from './phoneTopBar';

const useStyles = makeStyles((theme) => ({
  // A light band under the buttons. On a phone-sized screen the row is the
  // right-hand part of the viewer's top bar (see phoneTopBar) instead: its
  // buttons centred on the line the info button sits on, over the viewer's
  // shade, which fades out where a band would end in a hard edge.
  bar: {
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    [theme.breakpoints.down('sm')]: {
      minHeight: PHONE_BAR_HEIGHT,
      alignItems: 'center',
      padding: PHONE_BAR_INSET,
      backgroundColor: 'transparent',
    },
  },
  // While the info button is drawn (not in fullscreen, not without the info
  // capability), the row leaves it its room on the left. After `bar`, so it
  // wins over bar's padding.
  barWithInfo: {
    [theme.breakpoints.down('sm')]: {
      paddingLeft: PHONE_BAR_INFO_ROOM,
    },
  },
  actionIconButton: {
    [theme.breakpoints.down('sm')]: {
      padding: (PHONE_BAR_BUTTON - 24) / 2,
    },
  },
  // The three reaction buttons, in the row with the tools. On a phone-sized
  // screen the viewer shows the tags in a row of their own at the bottom of
  // the photo (see ImagePreviewDialog), which leaves the reactions room
  // here, on the info button's line.
  reactions: {
    display: 'flex',
  },
  emojiIcon: {
    fontSize: '1.25rem',
    lineHeight: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 24,
    height: 24,
  },
}));

// eslint-disable-next-line react/prop-types
export function DialogActions({
  handleOpenDescriptionDialog,
  handleRotate,
  onDownloadImage,
  handleReactToFilepermission,
  onOpenDeleteDialog,
  handleSaveRotation,
  src,
  showSaveButton,
  updatePending,
  rotation,
  isVideo,
  hasPermissionToEdit,
  // Hosts whose delete permission differs from their edit permission (the
  // project bank) pass this; otherwise delete follows edit as before.
  hasPermissionToDelete = hasPermissionToEdit,
  toggleFullScreen,
  isFullScreen,
  filePermissionId,
  projectId,
  canViewActivityLogs,
  onOpenViewedBy,
  // What the viewer offers (see imagePreviewCapabilities). On a phone-sized
  // screen the viewer passes `tags: false`: the media tag chips and the tag
  // input would crowd the buttons there, and the row at the bottom of the
  // photo (MobileMediaTags) shows the tags instead.
  capabilities = IMAGE_PREVIEW_CAPABILITIES_ALL,
}) {
  const classes = useStyles();
  const floorplans = useSelector(selectProjectFloorplansArray);
  const hasFloorplans = floorplans.length > 0;

  const [popoverAnchor, setPopoverAnchor] = useState(null);
  const [openPopover, setOpenPopover] = useState(null);
  const [spinnerDownloadFile, setSpinnerDownloadFile] = useState(false);
  const [placeOnFloorplanOpen, setPlaceOnFloorplanOpen] = useState(false);
  const editId = openPopover || undefined;

  const { t } = useTranslation();

  const handleOpenPopover = (event, popoverName) => {
    setPopoverAnchor(event.currentTarget);
    setOpenPopover(popoverName);
  };

  const handleClosePopover = () => {
    setPopoverAnchor(null);
    setOpenPopover(null);
  };

  const handleReaction = (emoji) => {
    handleReactToFilepermission(emoji);
    handleClosePopover();
  };

  return (
    <div
      className={clsx(
        classes.bar,
        capabilities.info && !isFullScreen && classes.barWithInfo
      )}
      style={{
        display: 'flex',
        // Lets a row too long for the width wrap instead of running out past
        // the left edge: the tags and the open add-tag field from md up, a
        // floorplan button and "Tallenna" on a narrow phone.
        flexWrap: 'wrap',
        justifyContent: 'end',
      }}
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
      onWheel={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onMouseMove={(e) => e.stopPropagation()}
      onMouseUp={(e) => e.stopPropagation()}
      onMouseLeave={(e) => e.stopPropagation()}
    >
      {capabilities.tags ? <ImageMediaTags /> : null}
      {capabilities.floorplans &&
      !isVideo &&
      hasPermissionToEdit &&
      hasFloorplans ? (
        <IconButton
          className={classes.actionIconButton}
          onClick={() => setPlaceOnFloorplanOpen(true)}
          size="medium"
        >
          <Icon path={mdiMapMarkerPlus} size={1} />
        </IconButton>
      ) : null}
      {capabilities.edit ? (
        <IconButton
          className={classes.actionIconButton}
          onClick={(e) => handleOpenPopover(e, 'edit')}
          size="medium"
        >
          <Icon path={mdiPencil} size={1} />
        </IconButton>
      ) : null}
      {capabilities.interactions ? (
        <div className={classes.reactions}>
          <IconButton
            className={classes.actionIconButton}
            onClick={() => handleReactToFilepermission('👍')}
            size="medium"
          >
            <span
              role="img"
              aria-label="thumbsup"
              className={classes.emojiIcon}
            >
              👍
            </span>
          </IconButton>
          <IconButton
            className={classes.actionIconButton}
            onClick={() => handleReactToFilepermission('👎')}
            size="medium"
          >
            <span
              role="img"
              aria-label="thumbsdown"
              className={classes.emojiIcon}
            >
              👎
            </span>
          </IconButton>
          <IconButton
            className={classes.actionIconButton}
            onClick={(e) => handleOpenPopover(e, 'emoji')}
            size="medium"
          >
            <Icon path={mdiEmoticon} size={1} />
          </IconButton>
        </div>
      ) : null}
      <IconButton
        className={classes.actionIconButton}
        onClick={onDownloadImage}
        size="medium"
      >
        <Icon path={mdiDownload} size={1} />
      </IconButton>
      {canViewActivityLogs ? (
        <IconButton
          className={classes.actionIconButton}
          onClick={onOpenViewedBy}
          size="medium"
        >
          <Icon path={mdiEyeCircleOutline} size={1} />
        </IconButton>
      ) : null}
      {!isVideo ? (
        <IconButton
          className={`${classes.fullScreenButton} ${classes.actionIconButton}`}
          onClick={() => toggleFullScreen()}
        >
          {!isFullScreen ? (
            <FullscreenIcon></FullscreenIcon>
          ) : (
            <FullscreenExitIcon></FullscreenExitIcon>
          )}
        </IconButton>
      ) : null}
      <Popover
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        id={editId}
        open={openPopover === 'edit'}
        anchorEl={popoverAnchor}
        onClose={handleClosePopover}
        disablePortal
      >
        <Grid item className={classes.tableGrid}>
          {spinnerDownloadFile ? (
            <Spinner />
          ) : (
            <div className={classes.actionButtons}>
              <IconButton
                disabled={!hasPermissionToEdit}
                onClick={() => handleOpenDescriptionDialog()}
                size="medium"
              >
                <Icon path={mdiFormatText} size={1} />
              </IconButton>

              {!isVideo ? (
                <>
                  <IconButton onClick={() => handleRotate(-90)} size="medium">
                    <Icon path={mdiRotateLeftVariant} size={1} />
                  </IconButton>
                  <IconButton onClick={() => handleRotate(90)} size="medium">
                    <Icon path={mdiRotateRightVariant} size={1} />
                  </IconButton>{' '}
                </>
              ) : null}

              <IconButton
                disabled={!hasPermissionToDelete}
                color="secondary"
                onClick={() => onOpenDeleteDialog()}
                size="medium"
              >
                <Icon path={mdiDelete} size={1} />
              </IconButton>
            </div>
          )}
        </Grid>
      </Popover>
      <Popover
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        disablePortal
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        id={editId}
        open={openPopover === 'emoji'}
        anchorEl={popoverAnchor}
        onClose={handleClosePopover}
      >
        <Grid item className={classes.tableGrid}>
          {spinnerDownloadFile ? (
            <Spinner />
          ) : (
            <ReactionPalette
              reactToImage={handleReaction}
              activeReaction={undefined}
            />
          )}
        </Grid>
      </Popover>
      {capabilities.floorplans ? (
        <PlaceOnFloorplanDialog
          open={placeOnFloorplanOpen}
          onClose={() => setPlaceOnFloorplanOpen(false)}
          filePermissionId={filePermissionId}
          projectId={projectId}
        />
      ) : null}
      {showSaveButton() && src && hasPermissionToEdit ? (
        <Button
          color="primary"
          onClick={() => handleSaveRotation(rotation)}
          disabled={updatePending}
        >
          {t('common.save')}
        </Button>
      ) : null}
    </div>
  );
}
